/* Zap Form core - accessible validation + UTM capture + fetch to an inbound webhook.
   Generic form-builder engine (no vendor lock-in). Add-only, no dependency on theme JS.
   Contract: aria-invalid only when active, error linked via aria-describedby only while active,
   live region as a direct child of the form, focus to first error, submit blocked while invalid. */
(function () {
  "use strict";

  var EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
  var UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
  var summaryUid = 0;

  /* Gateway rejections are keyed by the response's `code` member (RFC 9457 problem body),
     never by its human-readable strings, which the server is free to reword. */
  var GATEWAY_MESSAGES = {
    invalid_payload: "Some details could not be validated. Please review the form and try again.",
    body_too_large: "Your message is too long. Please shorten it and try again.",
    unknown_form_key: "This form is temporarily unavailable. Please try again later.",
    form_unavailable: "This form is temporarily unavailable. Please try again later.",
    captcha_failed: "We could not verify your submission. Please try again.",
    captcha_expired: "Verification expired - please submit again.",
    captcha_unavailable: "Verification is temporarily unavailable. Please try again in a moment.",
    delivery_failed: "We could not deliver your message. Please try again."
  };
  var GENERIC_FAIL_MESSAGE = "Something went wrong. Please try again.";
  var turnstileApi = null;

  function ctrlOf(field) {
    return field.closest("[data-zap-form-control]") || field.parentNode;
  }

  function showError(field, message) {
    var ctrl = ctrlOf(field);
    var errId = field.id + "-error";
    var node = document.getElementById(errId);
    if (!node) {
      node = document.createElement("p");
      node.className = "zap-form_error";
      node.id = errId;
      node.setAttribute("aria-live", "assertive");
      node.setAttribute("aria-atomic", "true");
      ctrl.appendChild(node);
    }
    node.textContent = message;
    field.setAttribute("aria-invalid", "true");
    var base = field.getAttribute("data-zap-form-hint");
    field.setAttribute("aria-describedby", base ? base + " " + errId : errId);
    ctrl.classList.add("zap-form_invalid");
  }

  function clearError(field) {
    var ctrl = ctrlOf(field);
    var errId = field.id + "-error";
    var node = document.getElementById(errId);
    if (node) node.remove();
    field.setAttribute("aria-invalid", "false");
    var base = field.getAttribute("data-zap-form-hint");
    if (base) field.setAttribute("aria-describedby", base);
    else field.removeAttribute("aria-describedby");
    ctrl.classList.remove("zap-form_invalid");
  }

  function labelText(field) {
    var lab = field.labels && field.labels[0];
    var t = lab ? lab.textContent : field.getAttribute("aria-label") || field.name || "This field";
    // Consent labels are written as full sentences ("I agree to be contacted about this
    // inquiry."), so appending " is required." produced "...inquiry. is required." Strip a
    // trailing period before the message is built.
    return t.replace(/\*/g, "").trim().replace(/\.\s*$/, "");
  }

  /* Checkbox and radio validation.
     THE BUG THIS FIXES: a checkbox carries its `value` attribute whether or not it is
     checked, so the emptiness test below (`required && !val`) could never fail one. A
     required consent box submitted happily while unchecked. For a choice control,
     required means CHECKED, never non-empty.
     Group handling follows knowledge/zenyth-patterns/forms.md: one error for the whole
     group, placed after the group wrapper, with aria-describedby shared by every member
     so it is announced no matter which option has focus. */
  function choiceGroup(field) {
    var wrap = field.closest('[role="group"][data-zap-group-name], [role="radiogroup"]');
    if (wrap) {
      return { wrap: wrap, members: Array.prototype.slice.call(wrap.querySelectorAll('input[type="checkbox"], input[type="radio"]')) };
    }
    if (field.type === "radio" && field.name && field.form) {
      return { wrap: null, members: Array.prototype.slice.call(field.form.querySelectorAll('input[type="radio"][name="' + field.name + '"]')) };
    }
    return { wrap: null, members: [field] };
  }

  function choiceLabel(group, field) {
    if (group.wrap) {
      var legend = group.wrap.getAttribute("aria-labelledby");
      var el = legend ? document.getElementById(legend) : null;
      if (el) return (el.textContent || "").replace(/\*/g, "").trim();
    }
    return labelText(field);
  }

  function setChoiceError(group, members, anchor, message) {
    var errId = anchor.id + "-error";
    var node = document.getElementById(errId);
    if (!node) {
      node = document.createElement("p");
      node.className = "zap-form_error";
      node.id = errId;
      node.setAttribute("aria-live", "assertive");
      node.setAttribute("aria-atomic", "true");
      // after the group, or inside the control for a standalone checkbox
      if (group.wrap) group.wrap.parentNode.insertBefore(node, group.wrap.nextSibling);
      else ctrlOf(anchor).appendChild(node);
    }
    node.textContent = message;
    members.forEach(function (m) {
      m.setAttribute("aria-invalid", "true");
      var base = m.getAttribute("data-zap-form-hint");
      m.setAttribute("aria-describedby", base ? base + " " + errId : errId);
    });
    (group.wrap || ctrlOf(anchor)).classList.add("zap-form_invalid");
  }

  function clearChoiceError(group, members, anchor) {
    var node = document.getElementById(anchor.id + "-error");
    if (node) node.remove();
    members.forEach(function (m) {
      m.setAttribute("aria-invalid", "false");
      var base = m.getAttribute("data-zap-form-hint");
      if (base) m.setAttribute("aria-describedby", base);
      else m.removeAttribute("aria-describedby");
    });
    (group.wrap || ctrlOf(anchor)).classList.remove("zap-form_invalid");
  }

  function validateChoice(field) {
    var group = choiceGroup(field);
    var members = group.members.filter(function (m) { return !m.closest("[hidden]"); });
    if (!members.length) return true;
    // Report once per group: `fields()` hands us every member, and four identical errors
    // in the summary for one group is noise, not help.
    if (members[0] !== field) return true;

    var required = members.some(function (m) {
      return m.hasAttribute("required") || m.getAttribute("aria-required") === "true";
    });
    // A checkbox GROUP carries aria-required on the wrapper, not on its children.
    if (!required && group.wrap && group.wrap.getAttribute("aria-required") === "true") required = true;

    if (required && !members.some(function (m) { return m.checked; })) {
      var name = choiceLabel(group, field);
      setChoiceError(group, members, field,
        members.length > 1 ? name + ": select at least one option." : name + " is required.");
      return false;
    }
    clearChoiceError(group, members, field);
    return true;
  }

  function validateField(field) {
    if (field.type === "checkbox" || field.type === "radio") return validateChoice(field);

    var val = (field.value || "").trim();
    var required = field.hasAttribute("required") || field.getAttribute("aria-required") === "true";
    if (required && !val) {
      showError(field, labelText(field) + " is required.");
      return false;
    }
    if (field.type === "email" && val && !EMAIL_RE.test(val)) {
      showError(field, "Enter a valid email (name@domain.com).");
      return false;
    }
    clearError(field);
    return true;
  }

  function liveRegion(form) {
    var live = form.querySelector(".zap-form_live");
    if (!live) {
      live = document.createElement("p");
      live.className = "zap-form_live";
      live.setAttribute("aria-live", "polite");
      live.setAttribute("aria-atomic", "true");
      form.appendChild(live);
    }
    return live;
  }

  function fields(form) {
    return Array.prototype.slice
      .call(form.querySelectorAll("input, select, textarea"))
      .filter(function (f) {
        // Skip hidden helpers, and any control hidden by option-driven visibility (inside [hidden]) -
        // those must not be validated or sent.
        return f.type !== "hidden" && f.type !== "submit" && f.type !== "button" && !f.hasAttribute("data-zap-form-hp") && !f.closest("[hidden]");
      });
  }

  function optionFieldList(opt) {
    var raw = opt ? (opt.getAttribute("data-zap-option-fields") || "") : "";
    return raw.split(",").map(function (s) { return s.trim(); }).filter(Boolean);
  }

  // Option-driven field visibility (storefront, and the theme editor when the select's switch is OFF):
  // the selected option's data-zap-option-fields lists which field keys stay; a blank/absent list shows
  // all; anything else is hidden with [hidden] (also dropped from validation/payload via fields()).
  function applyOptionFields(form, sel) {
    // In the editor, when this select's "show conditional fields" switch is ON, the conditional editor
    // shows everything with option-name badges instead - don't run the live filter.
    var host = sel.closest("[data-zap-cond-select]");
    if (window.Shopify && window.Shopify.designMode && host && host.getAttribute("data-zap-show-conditional") === "1") return;
    var list = optionFieldList(sel.options[sel.selectedIndex]);
    Array.prototype.slice.call(form.querySelectorAll("[data-zap-field-key]")).forEach(function (ctrl) {
      if (ctrl.contains(sel)) return; // never hide the driving select's own control
      if (!list.length) { ctrl.hidden = false; return; }
      ctrl.hidden = list.indexOf(ctrl.getAttribute("data-zap-field-key")) === -1;
    });
  }

  function clearCondBadges(fc) {
    Array.prototype.slice.call(fc.querySelectorAll("[data-zap-cond-badge]")).forEach(function (b) { b.remove(); });
  }

  // Theme-editor aid (design mode ONLY). Switch ON: show EVERY field, and stamp on each conditional
  // field a badge per option that renders it (e.g. [Accessibility audit][Compliance program]) so the
  // whole set is visible at once. Switch OFF: clear the badges and let the select drive show/hide live -
  // changing the option hides/shows correctly, exactly like the storefront.
  // Per-option visual identity for the editor badges: a DISTINCT color for any number of options plus
  // a non-color cue (border style + a CSS-drawn shape chip) so they're told apart without relying on
  // color alone. No emoji (house rule) - the shape is CSS-drawn. Hues are spread by the golden angle so
  // even 10+ options stay well separated; it's deterministic by option order (stable, never flickers).
  var COND_BORDERS = ["solid", "dashed", "dotted", "double"];
  var COND_SHAPES = [
    { r: "50%", rot: false },        // circle
    { r: "2px", rot: false },        // square
    { r: "2px", rot: true },         // diamond
    { r: "50% 50% 50% 0", rot: false } // teardrop
  ];
  function condStyle(i) {
    var hue = Math.round((i * 137.508) % 360);
    var sh = COND_SHAPES[i % COND_SHAPES.length];
    return {
      fg: "hsl(" + hue + ",58%,34%)",
      bg: "hsl(" + hue + ",72%,94%)",
      bd: "hsl(" + hue + ",48%,72%)",
      bs: COND_BORDERS[i % COND_BORDERS.length],
      r: sh.r,
      rot: sh.rot
    };
  }

  function applyConditionalEditor(form) {
    if (!(window.Shopify && window.Shopify.designMode)) return;
    Array.prototype.slice.call(form.querySelectorAll("[data-zap-cond-select]")).forEach(function (host) {
      var sel = host.querySelector("select");
      if (!sel) return;
      var map = {};      // field key -> [option labels that render it]
      var styleOf = {};  // option label -> palette entry (stable per option, by order)
      var oi = 0;
      Array.prototype.slice.call(sel.options).forEach(function (o) {
        var fl = optionFieldList(o);
        if (!fl.length) return;
        var label = (o.textContent || "").trim();
        if (!(label in styleOf)) { styleOf[label] = condStyle(oi); oi++; }
        fl.forEach(function (k) { (map[k] = map[k] || []).push(label); });
      });
      if (!Object.keys(map).length) return;
      var showAll = host.getAttribute("data-zap-show-conditional") === "1";
      var controls = Array.prototype.slice.call(form.querySelectorAll("[data-zap-field-key]"));
      if (!showAll) {
        // Editor default: no badges, EVERYTHING visible so the form never starts empty. The select
        // drives hide/show only when the operator actually changes it (the change listener runs
        // applyOptionFields, which is allowed in the editor while this switch is off).
        controls.forEach(function (fc) { clearCondBadges(fc); fc.style.opacity = ""; fc.hidden = false; });
        return;
      }
      controls.forEach(function (fc) {
        if (fc.contains(sel)) return;
        clearCondBadges(fc);
        fc.hidden = false;
        var labels = map[fc.getAttribute("data-zap-field-key")];
        if (labels && labels.length) {
          fc.style.opacity = "0.75";
          // insertBefore(firstChild) reverses insertion order; reverse the labels to keep source order.
          labels.slice().reverse().forEach(function (l) {
            var p = styleOf[l] || condStyle(0);
            var b = document.createElement("span");
            b.setAttribute("data-zap-cond-badge", "");
            b.style.cssText = "display:inline-flex;align-items:center;margin:0 6px 6px 0;padding:3px 7px;border-radius:6px;font:700 10px/1 ui-sans-serif,system-ui,sans-serif;letter-spacing:.04em;text-transform:uppercase;color:" + p.fg + ";background:" + p.bg + ";border:1px " + p.bs + " " + p.bd + ";";
            var dot = document.createElement("span");
            dot.setAttribute("aria-hidden", "true");
            dot.style.cssText = "display:inline-block;width:7px;height:7px;margin-right:5px;background:" + p.fg + ";border-radius:" + p.r + ";" + (p.rot ? "transform:rotate(45deg);" : "");
            b.appendChild(dot);
            b.appendChild(document.createTextNode(l));
            fc.insertBefore(b, fc.firstChild);
          });
        } else {
          fc.style.opacity = "";
        }
      });
    });
  }

  function resolveWebhook(form) {
    var sel = form.querySelector("[data-zap-form-subject]");
    if (sel) {
      var opt = sel.options[sel.selectedIndex];
      if (opt && opt.dataset.webhook) return opt.dataset.webhook;
    }
    return form.dataset.webhook || "";
  }

  /* Gateway routing (data-zap-form-key): the form posts to the shared gateway, which verifies
     Turnstile server-side and forwards to the webhook mapped to the key. Active only when the
     key AND both theme settings are present - a half-configured form falls back to the legacy
     webhook path instead of stranding live leads. */
  function gatewayConfig(form) {
    var key = form.dataset.zapFormKey;
    var url = form.dataset.zapGateway;
    var sitekey = form.dataset.zapTurnstileSitekey;
    if (!key || !url || !sitekey) return null;
    return { key: key, url: url, sitekey: sitekey };
  }

  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (turnstileApi) return turnstileApi;
    turnstileApi = new Promise(function (resolve, reject) {
      window.zapTurnstileOnload = function () { resolve(window.turnstile); };
      var s = document.createElement("script");
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=zapTurnstileOnload&render=explicit";
      s.async = true;
      s.defer = true;
      s.onerror = function () { turnstileApi = null; reject(new Error("Turnstile api.js failed to load")); };
      document.head.appendChild(s);
    });
    return turnstileApi;
  }

  /* Explicit render with execution deferred to submit time: the token lives 300 s and is
     single-use, so running the challenge at page load would expire under a slow visitor
     (the wizard exceeds 300 s by design). The container stays display:none (it would occupy
     a flex-gap slot in the column otherwise) and is revealed only for the rare interactive
     challenge, via before-interactive-callback. */
  function setupTurnstile(form, cfg) {
    var container = form.querySelector("[data-zap-turnstile]");
    if (!container) return;
    loadTurnstile().then(function (ts) {
      var state = { widgetId: null, pending: null };
      function settle(deliver) {
        container.classList.remove("zap-form_turnstile-active");
        var pending = state.pending;
        state.pending = null;
        if (pending) deliver(pending);
      }
      state.widgetId = ts.render(container, {
        sitekey: cfg.sitekey,
        appearance: "interaction-only",
        execution: "execute",
        "before-interactive-callback": function () {
          container.classList.add("zap-form_turnstile-active");
        },
        callback: function (token) {
          settle(function (p) { p.resolve(token); });
        },
        "error-callback": function () {
          settle(function (p) { p.reject(new Error("Turnstile error")); });
          return true;
        },
        "timeout-callback": function () {
          settle(function (p) { p.reject(new Error("Turnstile timeout")); });
        }
      });
      form._zapTurnstile = state;
    }).catch(function () { /* form._zapTurnstile stays unset; submit reports verification unavailable */ });
  }

  function turnstileToken(form) {
    var state = form._zapTurnstile;
    if (!state || state.widgetId == null || !window.turnstile) {
      return Promise.reject(new Error("Turnstile unavailable"));
    }
    return new Promise(function (resolve, reject) {
      state.pending = { resolve: resolve, reject: reject };
      // Tokens are single-use: reset-then-execute so every attempt (including a resubmit
      // after captcha_expired or delivery_failed) gets a fresh run.
      try {
        window.turnstile.reset(state.widgetId);
        window.turnstile.execute(state.widgetId);
      } catch (err) {
        state.pending = null;
        reject(err);
      }
    });
  }

  function lockSubmit(form, locked) {
    Array.prototype.slice.call(form.querySelectorAll('button[type="submit"]')).forEach(function (b) {
      b.disabled = locked;
    });
  }

  function buildPayload(form) {
    var data = {};
    fields(form).forEach(function (f) {
      if (!f.name) return;
      if ((f.type === "radio" || f.type === "checkbox") && !f.checked) return;
      var v = (f.value || "").trim();
      // Checkbox groups share a name: accumulate every checked value as a comma-separated list.
      if (f.type === "checkbox" && data[f.name] != null && data[f.name] !== "") {
        data[f.name] = data[f.name] + ", " + v;
      } else {
        data[f.name] = v;
      }
    });
    Array.prototype.slice.call(form.querySelectorAll('input[type="hidden"][name]')).forEach(function (f) {
      // Turnstile injects its own hidden input (cf-turnstile-response) into the widget
      // container; the token travels as turnstile_token, never under Cloudflare's name.
      if (f.closest("[data-zap-turnstile]")) return;
      if (f.value) data[f.name] = f.value;
    });
    var sel = form.querySelector("[data-zap-form-subject]");
    if (sel) {
      var opt = sel.options[sel.selectedIndex];
      if (opt) data.subject = opt.value;
    }
    var params = new URLSearchParams(location.search);
    UTM_KEYS.forEach(function (k) {
      if (params.get(k)) data[k] = params.get(k);
    });
    // A form-level lead-source label, but never clobber a real "source" FIELD (e.g. "How did you hear")
    // that the fields loop already captured.
    if (!data.source) data.source = form.dataset.source || document.title;
    data.page_url = location.href;
    return data;
  }

  function succeed(form) {
    var msg = form.dataset.successMessage || "Thanks. We received your details and will be in touch.";
    var panel = document.createElement("div");
    panel.className = "zap-form_success";
    panel.setAttribute("role", "status");
    panel.setAttribute("tabindex", "-1");
    panel.textContent = msg;
    var redirect = form.dataset.successRedirect;
    // Replace the form with the success message: insert the panel, then remove the form entirely.
    form.parentNode.insertBefore(panel, form);
    form.remove();
    panel.focus();
    if (redirect) {
      setTimeout(function () { location.href = redirect; }, 1200);
    }
  }

  function submitPayload(form, payload, webhook) {
    if (window.dataLayer) {
      window.dataLayer.push({ event: "zap_form_lead", zap_form_source: payload.source, zap_form_subject: payload.subject || null });
    }
    return fetch(webhook, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });
  }

  function formWrap(form) { return form.closest(".zap-form") || form.parentNode; }

  function removeSummary(form) {
    var s = formWrap(form).querySelector(".zap-form_summary");
    if (s) s.remove();
  }

  // Error summary at the top of the form block but OUTSIDE the <form>, shown only after a submit with
  // errors: a count + a list of links that jump to each invalid field. Focus moves to it. It sits outside
  // the form (role=group, labelled by its title) so screen readers read it reliably as page content
  // instead of skipping it as static text between form controls.
  function buildSummary(form, invalid) {
    removeSummary(form);
    var titleId = "zap-form-summary-" + (++summaryUid);
    var sum = document.createElement("div");
    sum.className = "zap-form_summary";
    sum.setAttribute("role", "group");
    sum.setAttribute("tabindex", "-1");
    sum.setAttribute("aria-labelledby", titleId);
    var title = document.createElement("p");
    title.className = "zap-form_summary-title";
    title.id = titleId;
    title.textContent = invalid.length + (invalid.length === 1 ? " field needs attention." : " fields need attention.");
    sum.appendChild(title);
    var ul = document.createElement("ul");
    ul.className = "zap-form_summary-list";
    invalid.forEach(function (f) {
      var li = document.createElement("li");
      var a = document.createElement("a");
      a.href = "#" + f.id;
      a.textContent = labelText(f);
      a.addEventListener("click", function (ev) {
        ev.preventDefault();
        var t = document.getElementById(f.id);
        if (t) t.focus();
      });
      li.appendChild(a);
      ul.appendChild(li);
    });
    sum.appendChild(ul);
    // Outside the <form>: as the first child of the .zap-form wrapper, above the required note + form.
    var wrap = formWrap(form);
    wrap.insertBefore(sum, wrap.firstChild);
    sum.focus();
  }

  function onSubmit(e) {
    var form = e.currentTarget;
    e.preventDefault();

    var hp = form.querySelector("[data-zap-form-hp]");
    if (hp && hp.value) return;

    var all = fields(form);
    var invalid = all.filter(function (f) { return !validateField(f); });
    if (invalid.length) {
      buildSummary(form, invalid);
      return;
    }
    removeSummary(form);
    var live = liveRegion(form);
    var cfg = gatewayConfig(form);

    var webhook = "";
    if (!cfg) {
      webhook = resolveWebhook(form);
      if (!webhook || /REPLACE_WITH/.test(webhook)) {
        live.textContent = "Form not connected yet (missing webhook URL).";
        return;
      }
    }

    var payload = buildPayload(form);
    live.textContent = "Sending...";
    lockSubmit(form, true);

    function fail(message) {
      live.textContent = message;
      lockSubmit(form, false);
    }

    if (!cfg) {
      submitPayload(form, payload, webhook)
        .then(function (res) {
          if (res.ok) succeed(form);
          else fail(GENERIC_FAIL_MESSAGE);
        })
        .catch(function () { fail(GENERIC_FAIL_MESSAGE); });
      return;
    }

    turnstileToken(form).then(
      function (token) {
        payload.form_key = cfg.key;
        payload.turnstile_token = token;
        submitPayload(form, payload, cfg.url)
          .then(function (res) {
            if (res.ok) { succeed(form); return; }
            res.json().catch(function () { return {}; }).then(function (body) {
              fail(GATEWAY_MESSAGES[body && body.code] || GENERIC_FAIL_MESSAGE);
            });
          })
          .catch(function () { fail(GENERIC_FAIL_MESSAGE); });
      },
      function () { fail(GATEWAY_MESSAGES.captcha_unavailable); }
    );
  }

  function init(form) {
    if (form.dataset.zapFormInit) return;
    form.dataset.zapFormInit = "1";
    form.setAttribute("novalidate", "novalidate");
    liveRegion(form);
    // Radio/checkbox groups render their options as sub-blocks with no name (a child can't read the
    // parent group's field_key in Liquid). The group carries data-zap-group-name; propagate it as the
    // shared name onto each option input so validation and the payload work normally.
    Array.prototype.slice.call(form.querySelectorAll("[data-zap-group-name]")).forEach(function (grp) {
      var nm = grp.getAttribute("data-zap-group-name");
      if (!nm) return;
      Array.prototype.slice.call(grp.querySelectorAll("input[data-zap-form-choice]")).forEach(function (inp) {
        if (!inp.getAttribute("name")) inp.setAttribute("name", nm);
      });
    });
    fields(form).forEach(function (f) {
      if (!f.getAttribute("aria-invalid")) f.setAttribute("aria-invalid", "false");
      var isChoice = f.tagName === "SELECT" || f.type === "radio" || f.type === "checkbox";
      if (isChoice) {
        // Inline: validate a select/choice as soon as it changes.
        // For a checkbox or radio, revalidate the group's FIRST member, not the one that
        // changed: validateChoice reports once per group and returns early for the others,
        // so ticking option 3 would otherwise leave the group's error on screen after the
        // user had already fixed it.
        f.addEventListener("change", function () {
          if (f.type === "checkbox" || f.type === "radio") {
            var g = choiceGroup(f);
            var live = g.members.filter(function (m) { return !m.closest("[hidden]"); });
            validateField(live.length ? live[0] : f);
            return;
          }
          validateField(f);
        });
      } else {
        // Inline: validate on blur (when leaving the field)...
        f.addEventListener("blur", function () { validateField(f); });
        // ...then re-check live as the user fixes an already-flagged field.
        f.addEventListener("input", function () {
          if (f.getAttribute("aria-invalid") === "true") validateField(f);
        });
      }
    });
    // Wire option-driven visibility on any select whose options declare data-zap-option-fields,
    // and apply it once for the initial selection.
    Array.prototype.slice.call(form.querySelectorAll("select")).forEach(function (sel) {
      var driver = Array.prototype.some.call(sel.options, function (o) { return o.hasAttribute("data-zap-option-fields"); });
      if (!driver) return;
      sel.addEventListener("change", function () { applyOptionFields(form, sel); });
      // Apply the initial selection on the storefront only; in the editor the conditional editor
      // governs the initial view so it never starts with an empty form.
      if (!(window.Shopify && window.Shopify.designMode)) applyOptionFields(form, sel);
    });
    // URL-param prefill (opt-in; only acts when the link carries the params):
    //   ?subject=<option value>  preselects the subject dropdown and fires its show/hide logic
    //   ?custom_subject=<text>   prefills the custom_subject field (revealed by the chosen subject)
    //   ?product=<option value>  preselects the product dropdown (revealed by the chosen subject)
    // e.g. /pages/contact-demo?subject=shopify_plugins&product=gif_pause_solution
    if (!(window.Shopify && window.Shopify.designMode)) {
      try {
        var qp = new URLSearchParams(window.location.search);
        var subjVal = qp.get("subject");
        if (subjVal) {
          var subjSel = form.querySelector('select[name="subject"]');
          if (subjSel && Array.prototype.some.call(subjSel.options, function (o) { return o.value === subjVal; })) {
            subjSel.value = subjVal;
            subjSel.dispatchEvent(new Event("change", { bubbles: true }));
          }
        }
        var csVal = qp.get("custom_subject");
        if (csVal) {
          var csField = form.querySelector('[name="custom_subject"]');
          if (csField) csField.value = csVal;
        }
        var prodVal = qp.get("product");
        if (prodVal) {
          var prodSel = form.querySelector('select[name="product"]');
          if (prodSel && Array.prototype.some.call(prodSel.options, function (o) { return o.value === prodVal; })) {
            prodSel.value = prodVal;
            prodSel.dispatchEvent(new Event("change", { bubbles: true }));
          }
        }
        var ptVal = qp.get("platform_tier");
        if (ptVal) {
          var ptSel = form.querySelector('select[name="platform_tier"]');
          if (ptSel && Array.prototype.some.call(ptSel.options, function (o) { return o.value === ptVal; })) {
            ptSel.value = ptVal;
            ptSel.dispatchEvent(new Event("change", { bubbles: true }));
          }
        }
        var ctVal = qp.get("compliance_tier");
        if (ctVal) {
          var ctSel = form.querySelector('select[name="compliance_tier"]');
          if (ctSel && Array.prototype.some.call(ctSel.options, function (o) { return o.value === ctVal; })) {
            ctSel.value = ctVal;
            ctSel.dispatchEvent(new Event("change", { bubbles: true }));
          }
        }
      } catch (e) {}
    }
    // Turnstile only on keyed forms, and never in the theme editor (the editor's domain is
    // not in the widget's hostname allow-list, so rendering there would show an error box).
    var cfg = gatewayConfig(form);
    if (cfg && !(window.Shopify && window.Shopify.designMode)) setupTurnstile(form, cfg);
    applyConditionalEditor(form);
    form.addEventListener("submit", onSubmit);
  }

  function boot() {
    Array.prototype.slice.call(document.querySelectorAll("[data-zap-form]")).forEach(init);
  }

  if (document.readyState !== "loading") boot();
  else document.addEventListener("DOMContentLoaded", boot);

  // Theme editor: re-init when a section reloads (e.g. toggling the "Show conditional fields" switch)
  // so the design-mode conditional view updates live.
  document.addEventListener("shopify:section:load", function (e) {
    var scope = (e && e.target) || document;
    Array.prototype.slice.call(scope.querySelectorAll("[data-zap-form]")).forEach(init);
  });

  window.ZapForm = {
    validateField: validateField,
    buildPayload: buildPayload,
    resolveWebhook: resolveWebhook,
    submitPayload: submitPayload,
    succeed: succeed,
    liveRegion: liveRegion,
    init: init
  };
})();
